import type { SxProps, Theme } from '@mui/material/styles';

/**
 * No mobile, os atalhos de Suporte e Chat em equipe viram abas colapsadas na borda direita,
 * ancoradas acima do rodapé (sem cobrir a grade da agenda no meio da tela).
 */
export const MOBILE_EDGE_TAB_WIDTH = 40;
export const MOBILE_EDGE_TAB_HEIGHT = 68;
export const MOBILE_EDGE_TAB_GAP = 8;

export type MobileEdgeDockSlot = 'support' | 'team';

/** Base da aba inferior (Equipe) acima do safe-area — ajuste fino do “quão baixo” no mobile. */
export const MOBILE_EDGE_STACK_BOTTOM = 112;

export function mobileEdgeTabBottom(slot: MobileEdgeDockSlot): string {
  const inset = 'env(safe-area-inset-bottom, 0px)';
  const teamBottom = MOBILE_EDGE_STACK_BOTTOM;
  if (slot === 'team') {
    return `calc(${inset} + ${teamBottom}px)`;
  }
  return `calc(${inset} + ${teamBottom + MOBILE_EDGE_TAB_HEIGHT + MOBILE_EDGE_TAB_GAP}px)`;
}

/** Deslocamento vertical extra (px) salvo pelo usuário ao arrastar a aba. */
export function mobileEdgeTabBottomWithOffset(
  slot: MobileEdgeDockSlot,
  offsetPx: number,
): string {
  const base = mobileEdgeTabBottom(slot);
  if (!offsetPx) return base;
  return `calc(${base} + ${Math.round(offsetPx)}px)`;
}

export const mobileEdgeTabFixedSx: SxProps<Theme> = {
  position: 'fixed',
  right: 'env(safe-area-inset-right, 0px)',
  top: 'auto',
};

export const mobileEdgeTabButtonSx: SxProps<Theme> = {
  width: { xs: 36, sm: MOBILE_EDGE_TAB_WIDTH },
  height: { xs: 58, sm: MOBILE_EDGE_TAB_HEIGHT },
  minWidth: { xs: 36, sm: MOBILE_EDGE_TAB_WIDTH },
  minHeight: { xs: 58, sm: MOBILE_EDGE_TAB_HEIGHT },
  px: 0,
  py: { xs: 1, sm: 1.25 },
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  borderRadius: '14px 0 0 14px',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRight: 'none',
  boxShadow:
    '-4px 0 20px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.22)',
  fontFamily: 'inherit',
  fontSize: { xs: '0.52rem', sm: '0.58rem' },
  fontWeight: 800,
  letterSpacing: '0.1em',
  lineHeight: 1.15,
  textTransform: 'uppercase',
  transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
  '&:hover': {
    filter: 'brightness(1.06)',
    transform: 'translateX(-2px)',
  },
  '&:active': { transform: 'translateX(0)' },
  '&.Mui-focusVisible': {
    outline: '2px solid rgba(255, 255, 255, 0.92)',
    outlineOffset: -2,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
};

export const mobileEdgeTabLabelSx: SxProps<Theme> = {
  writingMode: 'vertical-rl',
  textOrientation: 'mixed',
  transform: 'rotate(180deg)',
  maxHeight: { xs: 38, sm: MOBILE_EDGE_TAB_HEIGHT - 20 },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
